import { useContext, useMemo, useCallback } from "react";
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from "../../../../Context/GroupReportContext";
import { applyFullTieOrder } from "../../../../utils/quadrantTieOrder";

export default function YourQuardrantsFlowPreview360({ pageNumber }) {
  const { averagePercentages, averagePercentagesSelf } = useContext(GroupReportContext);
  const normalize = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((el) => ({
        name: String(el?.name || "").toLowerCase(),
        value: Number(el?.avg ?? el?.value ?? 0),
      }))
      .filter((el) => ["heart", "soul", "mind", "strength"].includes(el.name));
  }, []);

  const selfFlowData = useMemo(() => {
    const data = normalize(averagePercentagesSelf || []);
    const mustHave = ["heart", "soul", "mind", "strength"];
    mustHave.forEach((n) => {
      if (!data.find((d) => d.name === n)) data.push({ name: n, value: 0 });
    });
    return data;
  }, [averagePercentagesSelf, normalize]);

  const aggregated360FlowData = useMemo(() => {
    const data = normalize(averagePercentages || []);
    const mustHave = ["heart", "soul", "mind", "strength"];
    mustHave.forEach((n) => {
      if (!data.find((d) => d.name === n)) data.push({ name: n, value: 0 });
    });
    return data;
  }, [averagePercentages, normalize]);

  const SquareWithArrow = ({ scores }) => {
    const topLeft = 'MIND';
    const topRight = 'STRENGTH';
    const bottomLeft = 'SOUL';
    const bottomRight = 'HEART';

    const topLeftValue = (scores?.find(s => s.name === 'mind')?.value || 0).toFixed(2);
    const topRightValue = (scores?.find(s => s.name === 'strength')?.value || 0).toFixed(2);
    const bottomLeftValue = (scores?.find(s => s.name === 'soul')?.value || 0).toFixed(2);
    const bottomRightValue = (scores?.find(s => s.name === 'heart')?.value || 0).toFixed(2);

    const labelStyle = { color: '#000', fontSize: '9px', fontWeight: '700', fontFamily: 'Helvetica, sans-serif', textTransform: 'uppercase', position: 'absolute', lineHeight: '13px' };
    const percentageStyle = { color: '#000', fontSize: '9px', fontWeight: '700', fontFamily: 'Helvetica, sans-serif', position: 'absolute', lineHeight: '13px' };

    const diagramSize = 160;
    const headLength = 12;
    const headWidth = 8;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;

    const quadrantCenters = {
      mind: { x: quadrantOffsetX, y: quadrantOffsetY },
      strength: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      soul: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      heart: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    const quadrantScores = [
      { name: 'mind', score: scores?.find(s => s.name === 'mind')?.value || 0 },
      { name: 'strength', score: scores?.find(s => s.name === 'strength')?.value || 0 },
      { name: 'soul', score: scores?.find(s => s.name === 'soul')?.value || 0 },
      { name: 'heart', score: scores?.find(s => s.name === 'heart')?.value || 0 }
    ];

    const sortedQuadrants = applyFullTieOrder(
      [...quadrantScores].sort((a, b) => b.score - a.score),
      (q) => q.name,
      (q) => q.score
    ).map(({ name, score }) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value: score
    }));

    const pathPoints = sortedQuadrants.map(step => quadrantCenters[step.name.toLowerCase()]);

    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedQuadrants[0]
      ? quadrantCenters[sortedQuadrants[0].name.toLowerCase()]
      : { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';

    if (sortedQuadrants.length > 1) {
      const lastIdx = sortedQuadrants.length - 1;
      const tip = quadrantCenters[sortedQuadrants[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedQuadrants[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: `${diagramSize}px`, height: `${diagramSize}px`, backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000' }}>

        <div style={{ ...labelStyle, top: '6px', left: '10px' }}>
          {topLeft}
        </div>
        <div style={{ ...labelStyle, top: '6px', right: '10px' }}>
          {topRight}
        </div>

        <div style={{ ...percentageStyle, top: '20px', left: '10px' }}>
          {topLeftValue}%
        </div>

        <div style={{ ...percentageStyle, top: '20px', right: '10px' }}>
          {topRightValue}%
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', left: '10px' }}>
          {bottomLeftValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', left: '10px' }}>
          {bottomLeft}
        </div>

        <div style={{ ...percentageStyle, bottom: '20px', right: '10px' }}>
          {bottomRightValue}%
        </div>
        <div style={{ ...labelStyle, bottom: '6px', right: '10px' }}>
          {bottomRight}
        </div>

        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000' }}></div>
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000' }}></div>

        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="6.5" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>
      </div>
    );
  };

  const getFlowText = (scores) => {
    if (!scores || scores.length === 0) return "YOU FLOW FROM HEART TO SOUL";

    const priority = { heart: 4, strength: 3, soul: 2, mind: 1 };

    // priority breaks partial ties; a full 4-way tie goes clockwise (Strength to Heart)
    const sorted = applyFullTieOrder(
      [...scores]
        .map((s) => ({ name: String(s.name).toLowerCase(), value: Number(s.value) || 0 }))
        .sort((a, b) => {
          const diff = b.value - a.value;
          if (diff !== 0) return diff;
          return (priority[b.name] || 0) - (priority[a.name] || 0);
        }),
      (s) => s.name,
      (s) => s.value
    );

    const first = (sorted[0]?.name || "heart").toUpperCase();
    const second = (sorted[1]?.name || "soul").toUpperCase();

    return `YOU FLOW FROM ${first} TO ${second}`;
  };

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)" }}>
      <HeaderPreview />
      <div className="quadrant-flow-container">
        <div className="about-report-section">
          <div className="content-wrapper">
            <div className="text-content">
              <h2 className="section-title">FLOW TYPE™</h2>
              <span className="subtitle">How You Move Through the Quadrants</span>
              <p>Alright, we have identified your Starter Quadrant, your home base. The moment you shift from that starting point, your Flow Type™ begins to emerge. The second quadrant you move into adds important nuance. Two people can share the same starting point yet operate very differently depending on where they go next.</p>
              <p>Some move from emotion to action. Others flow from vision to planning. The sequence shapes how you lead, relate, and respond under pressure.</p>
              <p>There are many possible Flow combinations based on how your energy moves through the quadrants. You do not need to memorize them. We focus on your specific pattern of four. The goal is to notice your natural rhythm, recognize your default patterns, and pay attention to the parts of you that tend to be overlooked or underused.</p>
              <p>Your last quadrant, the one you move into least, is often a blind spot or stretch area. It is not always a weakness. It is an invitation to recover a part of yourself that has gone quiet. Reclaiming that space often creates a deeper sense of integration.</p>
              <p>When you learn to move through all four quadrants of Heart, Soul, Mind, and Strength™, you gain access to your full design. That is the power of Flow. It is not about perfect balance. It is about becoming integrated, aware, and equipped to lead and live with greater impact.</p>
            </div>
            <p className="flow-results">YOUR FLOW RESULTS</p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width:"100%"}}>
              {/* SELF */}
              <div style={{ textAlign: "center", display: "flex", justifyContent: "start", alignItems: "center", flexDirection: "column" }}>
                <h4 style={{ color: "#00577D", margin: "0 0 8px 0px", fontFamily: "Helvetica", fontSize: "10px", fontWeight: 700, width: "160px", lineHeight: "14px" }}> SELF </h4>
                <SquareWithArrow scores={selfFlowData} />
                <p style={{ fontSize: "10px", color: "#00577D", marginTop: "8px", fontFamily: "Helvetica", lineHeight: "14px", fontWeight: 700 }}> {getFlowText(selfFlowData)} </p>
              </div>

              <div style={{ borderLeft: "1px solid #000000", height: "164px", alignSelf: "center", marginTop: "-11px" }} />

              {/* 360 */}
              <div style={{ textAlign: "center", display: "flex", justifyContent: "start", alignItems: "center", flexDirection: "column" }}>
                <h4 style={{ color: "#00577D", margin: "0 0 8px 0px", fontFamily: "Helvetica", fontSize: "10px", fontWeight: 700, width: "160px", lineHeight: "14px" }}>360</h4>
                <SquareWithArrow scores={aggregated360FlowData} />
                <p style={{ fontSize: '10px', color: '#00577D', marginTop: '8px', fontFamily: "Helvetica", lineHeight: "14px", fontWeight: 700 }}>{getFlowText(aggregated360FlowData)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterPreview reportType={"360 REPORT"} pageNumber={pageNumber} />
    </div>
  );
}
